import React from 'react';
import { TextField } from '@mui/material';
import { tokens } from '../../theme';

/**
 * The one text field.
 *
 * Phase 4 of the design-system plan. The audit measured six incompatible
 * treatments for "ask the user for a line of text":
 *
 *   dashboard search    h40  r4  1px #C4C4C4   Roboto 16
 *   new-document Title  h56  r4  no border     solid #D4AF37 fill
 *   editor toolbar      h37  r6  no border     Arial 18
 *   structure select    h30  r3  no border     solid #D4AF37 fill
 *   structure text      h38  r4  1px #E3E0F2   Inter 13.5
 *   cover designer      h24  r4  1px #CCCCCC   Arial 12
 *
 * Two of them are too short to hold a label. One is a solid gold block, the
 * only input of its kind anywhere in the product, sitting in the dialog every
 * new manuscript starts from.
 *
 * WHAT THIS ADDS BEYOND CONSISTENCY
 *
 *   - 44px, so a finger can hit it. Four of the six were below that.
 *   - A visible focus ring. The audit found 8 authored focus styles across
 *     112 keyboard stops; this is one of them, now applied everywhere.
 *   - An error state. No field in the product had one, so there was no way to
 *     tell someone what was wrong with what they typed.
 *
 * @param {'light'|'dark'} tone  which ground the field sits on
 * @param {string} error         message to show; presence turns on the state
 * All other props pass through to MUI's TextField.
 */
export default function Field({ tone = 'light', error, helperText, sx, ...rest }) {
  const dark = tone === 'dark';

  const c = {
    fg: dark ? '#FFFFFF' : tokens.color.ink,
    label: dark ? tokens.color.onDark : tokens.color.muted,
    bg: dark ? 'rgba(255,255,255,0.06)' : tokens.color.card,
    rest: dark ? 'rgba(255,255,255,0.35)' : tokens.color.line,
    hover: dark ? 'rgba(255,255,255,0.55)' : tokens.color.purpleMid,
    focus: dark ? tokens.color.goldFlat : tokens.color.purple,
    bad: dark ? tokens.color.errorOnDark : tokens.color.error,
  };

  const invalid = Boolean(error);
  const line = invalid ? c.bad : c.rest;

  return (
    <TextField
      fullWidth
      error={invalid}
      helperText={error || helperText}
      sx={{
        '& .MuiInputLabel-root': { color: c.label },
        '& .MuiInputLabel-root.Mui-focused': { color: invalid ? c.bad : c.focus },
        '& .MuiInputLabel-root.Mui-error': { color: c.bad },

        '& .MuiOutlinedInput-root': {
          backgroundColor: c.bg,
          color: c.fg,
          borderRadius: `${tokens.radius.base}px`,
          // 44px is the touch minimum; the label sits above, not inside, so
          // the control itself is the whole hit target.
          minHeight: 44,
          fontFamily: tokens.text.body.family,
          fontSize: tokens.text.body.size,
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: line,
          borderWidth: 1,
        },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: invalid ? c.bad : c.hover,
        },
        // The focus ring. Two pixels rather than one so it reads as a state
        // change and not a hover.
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: invalid ? c.bad : c.focus,
          borderWidth: 2,
        },
        '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
          borderColor: c.bad,
        },

        '& .MuiFormHelperText-root': {
          marginLeft: 0,
          fontFamily: tokens.text.body.family,
          fontSize: tokens.text.caption.size,
          color: c.label,
        },
        '& .MuiFormHelperText-root.Mui-error': { color: c.bad },

        '& input::placeholder, & textarea::placeholder': {
          color: c.label,
          opacity: 1,
        },
        ...sx,
      }}
      {...rest}
    />
  );
}
